import React, { useEffect, useMemo, useState } from "react";
import {
  Edit,
  Trash2,
  Eye,
  Mail,
  Phone,
  Calendar,
  Shield,
  Building,
  RefreshCw,
  UserPlus,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteUser,
  fetchUsers,
  resetMutationState,
  updateUser,
} from "../../../features/userManagement/userManagementSlice";
import EditUserModal from "./EditUserModal";
import PageLoader from "../../../components/Common/PageLoader";
import { DataTable } from "../../../components/Common/DataTable";
import { useClientFilter } from "../../../hooks/useClientFilter";

export default function UserManagement({ onClickAddUser }) {
  const [editingUser, setEditingUser] = useState(null);
  const dispatch = useDispatch();

  const {
    users,
    status,
    error,
    updateStatus,
    updateError,
    updatingUserId,
    deleteStatus,
    deleteError,
    deletingUserId,
  } = useSelector((state) => state.userManagement);

  // Use client filter hook for filtering by selected client
  const { selectedClientId } = useClientFilter();

  // Filter users by selected client (nested client.clientUUID)
  const filteredUsers = useMemo(() => {
    if (!users || !Array.isArray(users)) return [];
    if (!selectedClientId) return users; // Return all if no client selected
    
    return users.filter((user) => user.client?.clientUUID === selectedClientId);
  }, [users, selectedClientId]);

  const getUserInitials = (user) => {
    const first = (user.firstName || "").trim().charAt(0);
    const last = (user.lastName || "").trim().charAt(0);
    const initials = `${first}${last}`.trim();

    if (initials) {
      return initials.toUpperCase();
    }

    const emailInitial = (user.email || "").trim().charAt(0);
    return emailInitial ? emailInitial.toUpperCase() : "U";
  };

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchUsers());
    }
  }, [dispatch, status]);

  useEffect(() => {
    dispatch(resetMutationState());
    return () => {
      dispatch(resetMutationState());
    };
  }, [dispatch]);

  const getStatusClass = (status) => {
    const normalizedStatus = (status || "").toLowerCase();
    switch (normalizedStatus) {
      case "active":
        return "chip chip--success";
      case "inactive":
        return "chip chip--muted";
      case "suspended":
        return "chip chip--danger";
      default:
        return "chip chip--muted";
    }
  };

  const getRoleClass = (role) => {
    switch (role) {
      case "System Administrator":
      case "System Admin":
        return "badge badge--critical";
      case "Security Manager":
        return "badge badge--high";
      case "SOC Analyst":
        return "badge badge--low";
      default:
        return "badge";
    }
  };

  // Helper to get client name
  const getClientName = (clientData) => {
    if (!clientData) return "";
    if (typeof clientData === "object" && clientData.name) {
      return clientData.name;
    }
    return String(clientData || "");
  };

  // Transform users data for the table
  const tableData = useMemo(() => {
    return filteredUsers.map((user) => ({
      ...user,
      fullName:
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        user.email ||
        "Unknown User",
      clientName: getClientName(user.client) || "-",
      initials: getUserInitials(user),
      departmentDisplay: user.department || "-",
      statusDisplay: user.status || "Unknown",
      lastLoginDisplay: user.lastLogin || "Never",
      emailDisplay: user.email || "-",
      phoneDisplay: user.phone || "-",
      roleDisplay: user.role || "-",
    }));
  }, [filteredUsers]);

  const handleDeleteUser = (userId) => {
    if (deleteStatus === "loading" && deletingUserId === userId) {
      return;
    }
    if (window.confirm("Are you sure you want to delete this user?")) {
      dispatch(deleteUser(userId));
    }
  };

  const handleToggleStatus = (user) => {
    if (updateStatus === "loading" && updatingUserId === user.id) {
      return;
    }

    const isActive = (user.status || "").toLowerCase() === "active";
    const toggledStatus = isActive ? "Inactive" : "Active";

    const updatedUser = {
      ...user,
      status: toggledStatus,
      lastLogin: user.lastLogin ?? null,
      createdAt: user.createdAt ?? new Date().toISOString(),
    };

    dispatch(updateUser({ id: user.id, data: updatedUser }));
  };

  const handleRefresh = () => {
    dispatch(fetchUsers());
  };

  // Define table columns
  const columns = useMemo(
    () => [
      // ===== VISIBLE BY DEFAULT =====
      {
        accessorKey: "fullName",
        header: "User",
        size: 220,
        cell: ({ row }) => {
          const user = row.original;
          return (
            <div className="user-cell">
              <div className="user-cell__avatar">{user.initials}</div>
              <div className="user-cell__info">
                <span className="user-cell__name">{user.fullName}</span>
                <span className="user-cell__email">{user.emailDisplay}</span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "roleDisplay",
        header: "Role",
        size: 160,
        cell: ({ getValue }) => {
          const role = getValue();
          return (
            <span className={getRoleClass(role)}>
              <Shield size={12} />
              {role}
            </span>
          );
        },
      },
      {
        accessorKey: "clientName",
        header: "Client",
        size: 150,
        cell: ({ getValue }) => (
          <div className="inline">
            <Building size={12} className="icon-muted" />
            <span>{getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: "statusDisplay",
        header: "Status",
        size: 100,
        cell: ({ getValue }) => {
          const status = getValue();
          return <span className={getStatusClass(status)}>{status}</span>;
        },
      },
      {
        accessorKey: "lastLoginDisplay",
        header: "Last Login",
        size: 140,
        cell: ({ getValue }) => (
          <div className="inline">
            <Calendar size={12} className="icon-muted" />
            <span className="font-mono small">{getValue()}</span>
          </div>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 140,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => {
          const user = row.original;
          const isActive = (user.status || "").toLowerCase() === "active";
          const isDeleting =
            deleteStatus === "loading" && deletingUserId === user.id;
          const isUpdating =
            updateStatus === "loading" && updatingUserId === user.id;

          return (
            <div className="action-buttons">
              <button
                className="btn btn--outline btn--icon"
                title="Edit User"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingUser(user);
                }}
                disabled={isUpdating || isDeleting}
              >
                <Edit size={14} />
              </button>
              <button
                className="btn btn--outline btn--icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleStatus(user);
                }}
                title={`${isActive ? "Deactivate" : "Activate"} User`}
                disabled={isUpdating || isDeleting}
              >
                {isUpdating ? "..." : isActive ? "⏸️" : "▶️"}
              </button>
              <button
                className="btn btn--outline btn--icon btn--danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteUser(user.id);
                }}
                title="Delete User"
                disabled={isDeleting || isUpdating}
              >
                {isDeleting ? "..." : <Trash2 size={14} />}
              </button>
            </div>
          );
        },
      },

      // ===== HIDDEN BY DEFAULT =====
      {
        accessorKey: "id",
        header: "User ID",
        size: 120,
        cell: ({ getValue }) => (
          <span className="chip chip--muted font-mono">
            {getValue() || "-"}
          </span>
        ),
      },
      {
        accessorKey: "emailDisplay",
        header: "Email",
        size: 200,
        cell: ({ getValue }) => (
          <div className="inline">
            <Mail size={12} className="icon-muted" />
            <span className="font-mono small">{getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: "phoneDisplay",
        header: "Phone",
        size: 140,
        cell: ({ getValue }) => (
          <div className="inline">
            <Phone size={12} className="icon-muted" />
            <span>{getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: "departmentDisplay",
        header: "Department",
        size: 140,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        size: 160,
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return <span className="text-muted">-</span>;
          try {
            return (
              <span className="font-mono small">
                {new Date(value).toLocaleDateString()}
              </span>
            );
          } catch {
            return <span className="text-muted">-</span>;
          }
        },
      },
    ],
    [deleteStatus, deletingUserId, updateStatus, updatingUserId]
  );

  // Initial column visibility
  const initialColumnVisibility = useMemo(
    () => ({
      id: false,
      emailDisplay: false,
      phoneDisplay: false,
      departmentDisplay: false,
      createdAt: false,
    }),
    []
  );

  if (status === "loading") {
    return <PageLoader loading={true} />;
  }

  return (
    <div className="user-management">
      {/* Header */}
      <div className="user-management__header">
        <div>
          <h2 className="user-management__title">User Management</h2>
          <p className="user-management__subtitle">
            Manage system users and their permissions
          </p>
          {/* Status Messages */}
          {error && (
            <div className="api-status">
              <span className="api-status__error">❌ {error}</span>
            </div>
          )}
          {updateError && (
            <div className="api-status">
              <span className="api-status__error">❌ {updateError}</span>
            </div>
          )}
          {deleteError && (
            <div className="api-status">
              <span className="api-status__error">❌ {deleteError}</span>
            </div>
          )}
        </div>
        <div className="user-management__actions">
          <button
            className="btn btn--outline"
            onClick={handleRefresh}
            disabled={status === "loading"}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button className="btn btn--primary" onClick={onClickAddUser}>
            <UserPlus size={16} />
            Add New User
          </button>
        </div>
      </div>

      {/* DataTable */}
      <DataTable
        data={tableData}
        columns={columns}
        enableSearch={true}
        enableFilters={true}
        enableColumnVisibility={true}
        enableSorting={true}
        enablePagination={true}
        enableExport={true}
        enableDensity={true}
        pageSize={10}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        emptyMessage="No users found"
        loading={status === "loading"}
        initialColumnVisibility={initialColumnVisibility}
      />

      {/* Summary Stats */}
      <div className="user-management__stats">
        <div className="stat-item">
          <span className="stat-item__label">Total Users:</span>
          <span className="stat-item__value">{filteredUsers.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-item__label">Active Users:</span>
          <span className="stat-item__value">
            {
              filteredUsers.filter((u) => (u.status || "").toLowerCase() === "active")
                .length
            }
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-item__label">Administrators:</span>
          <span className="stat-item__value">
            {
              filteredUsers.filter((u) =>
                (u.role || "").toLowerCase().includes("admin")
              ).length
            }
          </span>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
        />
      )}
    </div>
  );
}
